import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Pencil, Plus, Trash2, Upload, X } from "lucide-react";
import { API } from "@/context/AuthContext";

const EMPTY = { title: "", short_description: "", full_description: "", image_url: "", is_active: true };

export default function JobsManager() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/jobs/all`, { withCredentials: true });
      setJobs(data);
    } catch { toast.error("Stellen konnten nicht geladen werden"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing("new"); setForm(EMPTY); };
  const openEdit = (j) => { setEditing(j.id); setForm({ title: j.title, short_description: j.short_description, full_description: j.full_description, image_url: j.image_url || "", is_active: j.is_active }); };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await axios.post(`${API}/jobs/upload`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      setForm(f => ({ ...f, image_url: data.url }));
      toast.success("Bild hochgeladen");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload fehlgeschlagen"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const save = async () => {
    if (!form.title.trim() || !form.short_description.trim() || !form.full_description.trim()) {
      toast.error("Titel, Kurzbeschreibung und Beschreibung sind Pflicht"); return;
    }
    setSaving(true);
    try {
      if (editing === "new") await axios.post(`${API}/jobs`, form, { withCredentials: true });
      else await axios.patch(`${API}/jobs/${editing}`, form, { withCredentials: true });
      toast.success("Stelle gespeichert"); close(); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Stelle wirklich löschen?")) return;
    try { await axios.delete(`${API}/jobs/${id}`, { withCredentials: true }); toast.success("Stelle gelöscht"); await load(); }
    catch { toast.error("Löschen fehlgeschlagen"); }
  };

  const toggleActive = async (j) => {
    try { await axios.patch(`${API}/jobs/${j.id}`, { ...j, is_active: !j.is_active }, { withCredentials: true }); await load(); }
    catch { toast.error("Status konnte nicht geändert werden"); }
  };

  const input = "w-full h-11 px-4 rounded-md border border-[#D9E0E6] bg-white text-sm text-[#1d1d1b] focus:outline-none focus:ring-2 focus:ring-[#00AAA0] focus:border-[#00AAA0]";
  const label = "block font-tech text-[10px] tracking-[0.22em] uppercase text-[#1d1d1b] mb-2";

  return (
    <div data-testid="jobs-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-[#1d1d1b]">Karriere / Stellenangebote</h2>
          <p className="text-sm text-[#1d1d1b]/70 mt-1">Aktive Stellen werden öffentlich unter /karriere angezeigt.</p>
        </div>
        <button onClick={openNew} data-testid="job-new-btn"
          className="flex items-center gap-2 px-5 h-10 rounded-md bg-[#2C57D2] hover:bg-[#00AAA0] text-white font-tech text-[11px] tracking-[0.18em] uppercase transition-colors">
          <Plus size={14} /> Neue Stelle
        </button>
      </div>

      <div className="border border-[#E3E8EC] rounded-lg overflow-hidden bg-white" data-testid="jobs-table">
        <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 bg-[#F7F9FA] border-b border-[#E3E8EC]">
          <span className="col-span-5 font-tech text-[10px] tracking-[0.22em] uppercase text-[#1d1d1b]">Titel</span>
          <span className="col-span-4 font-tech text-[10px] tracking-[0.22em] uppercase text-[#1d1d1b]">Slug</span>
          <span className="col-span-1 font-tech text-[10px] tracking-[0.22em] uppercase text-[#1d1d1b]">Status</span>
          <span className="col-span-2 font-tech text-[10px] tracking-[0.22em] uppercase text-[#1d1d1b] text-right">Aktion</span>
        </div>
        {loading ? (
          <div className="px-6 py-16 text-center text-[#1d1d1b]"><Loader2 className="animate-spin inline mr-2" size={16} /> Lade…</div>
        ) : jobs.length === 0 ? (
          <div className="px-6 py-16 text-center text-sm text-[#1d1d1b]">Noch keine Stellen angelegt.</div>
        ) : jobs.map((j, i) => (
          <div key={j.id} className={`grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-6 py-4 items-center ${i < jobs.length-1 ? "border-b border-[#EEF1F4]" : ""}`} data-testid={`job-row-${j.slug}`}>
            <div className="md:col-span-5 font-medium text-[#1d1d1b]">{j.title}</div>
            <div className="md:col-span-4 font-tech text-xs text-[#2C57D2] truncate">/karriere/{j.slug}</div>
            <button onClick={() => toggleActive(j)} data-testid={`job-toggle-${j.slug}`}
              className={`md:col-span-1 inline-flex px-2.5 py-1 rounded-full border font-tech text-[10px] tracking-[0.15em] uppercase self-start md:self-center transition-colors ${j.is_active ? "border-[#00AAA0] text-[#00AAA0] bg-[#00AAA0]/5" : "border-[#E3E8EC] text-[#1d1d1b]/60"}`}>
              {j.is_active ? "Aktiv" : "Inaktiv"}
            </button>
            <div className="md:col-span-2 flex items-center gap-2 md:justify-end">
              <button onClick={() => openEdit(j)} data-testid={`job-edit-${j.slug}`} className="w-9 h-9 flex items-center justify-center rounded-md border border-[#E3E8EC] text-[#1d1d1b] hover:border-[#00AAA0] hover:text-[#00AAA0] transition-colors"><Pencil size={14}/></button>
              <button onClick={() => remove(j.id)} data-testid={`job-delete-${j.slug}`} className="w-9 h-9 flex items-center justify-center rounded-md border border-[#E3E8EC] text-[#1d1d1b] hover:border-[#EF4444] hover:text-[#EF4444] transition-colors"><Trash2 size={14}/></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="job-form-modal">
          <div className="bg-white rounded-lg w-full max-w-3xl my-8 border border-[#E3E8EC]">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E3E8EC]">
              <h3 className="font-bold text-[#1d1d1b]">{editing === "new" ? "Neue Stelle anlegen" : "Stelle bearbeiten"}</h3>
              <button onClick={close} className="w-9 h-9 rounded-md border border-[#E3E8EC] flex items-center justify-center text-[#1d1d1b] hover:text-[#EF4444] hover:border-[#EF4444]" data-testid="job-form-close"><X size={14}/></button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <label className={label}>Titel *</label>
                <input className={input} value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} placeholder="z. B. Produktionsmitarbeiter (m/w/d)" data-testid="job-form-title" />
              </div>
              <div>
                <label className={label}>Kurzbeschreibung *</label>
                <textarea className={`${input} h-24 py-3 resize-y`} value={form.short_description} onChange={e => setForm(f => ({...f, short_description: e.target.value}))} placeholder="Ein bis zwei Sätze für die Kartenansicht" data-testid="job-form-short" />
              </div>
              <div>
                <label className={label}>Beitragsbild</label>
                <div className="flex items-center gap-4">
                  {form.image_url ? (
                    <div className="relative w-32 h-20 rounded-md overflow-hidden border border-[#E3E8EC]">
                      <img src={form.image_url.startsWith("http") ? form.image_url : `${process.env.REACT_APP_BACKEND_URL}${form.image_url}`} alt="" className="w-full h-full object-cover" />
                    </div>
                  ) : (
                    <div className="w-32 h-20 rounded-md border border-dashed border-[#D9E0E6] flex items-center justify-center text-[#1d1d1b]/50 text-xs">Kein Bild</div>
                  )}
                  <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={e => upload(e.target.files?.[0])} hidden data-testid="job-form-file" />
                  <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                    className="flex items-center gap-2 px-4 h-10 rounded-md border border-[#2C57D2] text-[#2C57D2] hover:bg-[#2C57D2] hover:text-white font-tech text-[11px] tracking-[0.18em] uppercase transition-colors disabled:opacity-60" data-testid="job-form-upload-btn">
                    {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Bild wählen
                  </button>
                  {form.image_url && <button type="button" onClick={() => setForm(f => ({...f, image_url: ""}))} className="text-xs text-[#1d1d1b]/60 hover:text-[#EF4444]">entfernen</button>}
                </div>
                <p className="mt-2 text-xs text-[#1d1d1b]/60">JPG / PNG / WEBP / GIF – max. 8 MB</p>
              </div>
              <div>
                <label className={label}>Volle Beschreibung * (HTML erlaubt: &lt;h3&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;strong&gt;)</label>
                <textarea className={`${input} h-72 py-3 resize-y font-mono text-xs`} value={form.full_description} onChange={e => setForm(f => ({...f, full_description: e.target.value}))} data-testid="job-form-full" />
              </div>
              <label className="inline-flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({...f, is_active: e.target.checked}))} className="w-4 h-4 accent-[#00AAA0]" data-testid="job-form-active" />
                <span className="text-sm text-[#1d1d1b]">Stelle ist aktiv und öffentlich sichtbar</span>
              </label>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#E3E8EC] bg-[#F7F9FA]">
              <button onClick={close} className="px-5 h-10 rounded-md border border-[#E3E8EC] text-[#1d1d1b] font-tech text-[11px] tracking-[0.18em] uppercase">Abbrechen</button>
              <button onClick={save} disabled={saving} data-testid="job-form-save"
                className="flex items-center gap-2 px-6 h-10 rounded-md bg-[#2C57D2] hover:bg-[#00AAA0] text-white font-tech text-[11px] tracking-[0.18em] uppercase transition-colors disabled:opacity-60">
                {saving && <Loader2 size={13} className="animate-spin" />} Speichern
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
